import React, { useState, useEffect, useRef } from 'react';
import type { PlacesType } from 'react-tooltip';

interface NumberCounterProps {
  value: number;
  duration?: number; // アニメーション時間（ミリ秒）
  className?: string;
  isNegative?: boolean;
  animateFromZero?: boolean; // 0からアニメーションするかどうか
  groupId?: string; // グループIDを直接渡す
  suffix?: string; // 後に付ける文字（%など）
  tooltipContent?: string; // ツールチップ用
  tooltipHtml?: string; // ツールチップ用（HTML直接指定、tooltipContentより優先）
  suppressTooltip?: boolean; // 親要素がツールチップを管理する場合にtrue
  tooltipPlace?: PlacesType;
  showTimeConversion?: boolean; // 時刻変換を表示するかどうか
  _animateSignChange?: boolean; // 正負転換アニメーション
  /** ×⇔% : →はアニメ、>は即時。%→× は 12%→100%→>×1→→90。×→% は ×80→→×1→>100%→→80% */
  bridgePercentModeTransition?: boolean;
}

export const NumberCounter: React.FC<NumberCounterProps> = ({ 
  value, 
  duration = 300, // デフォルトで0.3秒
  className = '',
  isNegative = false,
  animateFromZero = false, // デフォルトはfalse
  groupId = null,
  suffix = '',
  tooltipContent = '',
  tooltipHtml,
  suppressTooltip = false,
  tooltipPlace,
  showTimeConversion = false,
  _animateSignChange = false,
  bridgePercentModeTransition = false
}) => {

  // 前回のアニメーション変更時刻を記録するためにコンポーネントレベルで保持
  const lastAnimationChangeTime = useRef<number | null>(null);
  const isFirstRender = useRef(true);
  const mountTimeRef = useRef<number>(performance.now()); // コンポーネントのマウント時刻を記録
  const previousSuffixRef = useRef(suffix);
  const bridgeSecondLegTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** %→× の 1→目的アニメ後に小数アニメへ渡す整数比較の補正（integerAnimStart とズレるため） */
  const decimalCompareIntegerRef = useRef<number | null>(null);
  /** ブリッジ中のみ suffix を上書き（親は既に最終状態なので、%→× の % 表示中などに使用） */
  const [bridgeVisualSuffix, setBridgeVisualSuffix] = useState<string | null>(null);

  // 実際の表示値を計算
  const actualValue = isNegative ? -value : value;
  
  // 小数部分の計算
  const getDecimalPart = (num: number): { value: number, digits: number } => {
    const absNum = Math.abs(num);
    const decimal = absNum - Math.floor(absNum);
    
    // 小数点以下の文字列を取得（3桁まで確認）
    const decimalStr3 = decimal.toFixed(3).substring(2, 5);
    const decimalStr2 = decimal.toFixed(2).substring(2, 4);
    
    // 小数第3位以降に数字があるかどうかを判定（より多くの桁数で確認）
    const hasMoreThan2Decimals = decimal.toFixed(6) !== decimal.toFixed(2) + '0000';
    
    if (decimalStr2 === "00" && !hasMoreThan2Decimals) {
      // 小数なし（小数第3位以降も0）
      return { value: 0, digits: 0 };
    } else if (decimalStr2.endsWith("0") && !hasMoreThan2Decimals) {
      // 小数第1位まで（0-9の整数）
      return { value: Math.round(decimal * 10), digits: 1 };
    } else if (!hasMoreThan2Decimals) {
      // 小数第2位まで（0-99の整数）
      return { value: Math.round(decimal * 100), digits: 2 };
    } else {
      // 小数第3位以降がある場合は、小数第2位で四捨五入して判定
      const roundedDecimal = Math.round(decimal * 100) / 100;
      const roundedDecimalStr = roundedDecimal.toFixed(2).substring(2, 4);
      
      if (roundedDecimalStr === "00") {
        // 四捨五入の結果が.00になった場合でも、元の値に小数部分があれば2桁表示
        return { value: 0, digits: 2 };
      } else if (roundedDecimalStr.endsWith("0")) {
        return { value: Math.round(roundedDecimal * 10), digits: 1 };
      } else {
        return { value: Math.round(roundedDecimal * 100), digits: 2 };
      }
    }
  };

  // 初期状態での小数部分の表示
  const initialDecimalInfo = getDecimalPart(actualValue);
  const hasInitialDecimal = initialDecimalInfo.digits > 0;

  // 整数部分と小数部分を分けて状態管理
  const [displayIntegerPart, setDisplayIntegerPart] = useState(animateFromZero || isFirstRender.current ? 0 : Math.floor(Math.abs(actualValue)));
  const [displayDecimalPart, setDisplayDecimalPart] = useState(initialDecimalInfo.value);
  const [isAnimatingDecimal, setIsAnimatingDecimal] = useState(false);
  const [showDecimal, setShowDecimal] = useState(hasInitialDecimal);
  const [displayIsNegative, setDisplayIsNegative] = useState(animateFromZero || isFirstRender.current ? false : isNegative);
  
  // 前回の値と符号を記録
  const previousValue = useRef(animateFromZero || isFirstRender.current ? 0 : actualValue);
  const previousIsNegative = useRef(animateFromZero || isFirstRender.current ? false : isNegative);
  const startTime = useRef<number | null>(null);
  const animationFrameId = useRef<number | null>(null);
  
  // 小数部分のアニメーション用
  const decimalStartTime = useRef<number | null>(null);
  const decimalAnimationFrameId = useRef<number | null>(null);
  
  // 現在のアニメーション値を追跡
  const currentAnimationValue = useRef(actualValue);

  // グループIDを保持するためのref（アニメーション終了時のイベント発火に使用）
  const groupIdRef = useRef<string | null>(groupId);
  
  // グループIDが変更されたら更新
  useEffect(() => {
    groupIdRef.current = groupId;
  }, [groupId]);

  // 値が変わったときのアニメーション
  useEffect(() => {
    // 現在進行中のアニメーションをキャンセルする前に、最終値を設定
    if (bridgeSecondLegTimeoutId.current) {
      clearTimeout(bridgeSecondLegTimeoutId.current);
      bridgeSecondLegTimeoutId.current = null;
    }
    decimalCompareIntegerRef.current = null;
    setBridgeVisualSuffix(null);

    if (animationFrameId.current || decimalAnimationFrameId.current) {
      setDisplayIntegerPart(Math.floor(Math.abs(previousValue.current)));
      const prevDecimalInfo = getDecimalPart(previousValue.current);
      setDisplayDecimalPart(prevDecimalInfo.value);
    }
    
    // 現在進行中のアニメーションをキャンセルする
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    if (decimalAnimationFrameId.current) {
      cancelAnimationFrame(decimalAnimationFrameId.current);
      decimalAnimationFrameId.current = null;
    }
    
    // アニメーション状態をリセット
    startTime.current = null;
    decimalStartTime.current = null;
    // animateFromZeroが指定されているときは常に0からアニメーション
    const startValue = animateFromZero || isFirstRender.current ? 0 : previousValue.current;
    const endValue = actualValue;
    // 現在アニメーション中の値を更新
    currentAnimationValue.current = endValue;
    
    // 同じ値で符号も変わらず、かつゼロからのアニメーションでない場合はアニメーションしない
    // ただし、_animateSignChangeフラグが立っている場合は強制的にアニメーション
    if (startValue === endValue && previousIsNegative.current === isNegative && !animateFromZero && !_animateSignChange) {
      previousSuffixRef.current = suffix;
      return;
    }
    
    // 初回レンダリング後はフラグをfalseに
    isFirstRender.current = false;

    // ボタン連打検出 - 前回のアニメーション開始から短時間で新しい値変更があった場合
    const now = performance.now();
    const timeSinceLastAnimation = lastAnimationChangeTime.current ? now - lastAnimationChangeTime.current : Infinity;
    const timeSinceMounted = now - mountTimeRef.current;
    
    // リロード後1000ms以内は連打判定を無効にする
    const isRecentlyMounted = timeSinceMounted < 1000;
    const isRapidChange = timeSinceLastAnimation < 500 && !isRecentlyMounted; // 500ms以内の変更を連打と判断（ただしリロード直後を除く）
    
    // 現在の時間を記録
    lastAnimationChangeTime.current = now;
    
    // durationの設定 - フッターとシート一覧の集計値は連打/押しっぱなし時に0にする
    // パウチ自体の数値はアニメーションキャンセルの影響を受けない
    let effectiveDuration = duration;
    
    // グループサブトータルの場合の特別処理
    const isGroupSubtotal = groupIdRef.current && animateFromZero;
    
    // 符号が変わるかどうかを判定
    const signChanged = (startValue < 0) !== (endValue < 0);
    
    // 符号の処理
    if (!signChanged && isNegative) {
      setDisplayIsNegative(true);
    } else if (signChanged) {
      // 符号が変わる場合は、アニメーション関数内で制御するため、
      // 初期値だけ設定しておく（これはアニメーション関数で上書きされる）
      setDisplayIsNegative(startValue < 0);
      
      // タイムアウトは不要になった（アニメーション関数内で制御するため）
      // setTimeout 部分を削除
    } else {
      // 符号が変わらない場合は即座に更新
      setDisplayIsNegative(endValue < 0);
    }

    // 連打時でduration=0の場合、または、グループサブトータル以外で連打時には即座に最終値を設定
    if (effectiveDuration === 0 || (isRapidChange && !isGroupSubtotal)) {
      // 整数部分を即座に設定
      setDisplayIntegerPart(Math.floor(Math.abs(endValue)));
      
      // 小数部分を計算
      const endDecimalInfo = getDecimalPart(endValue);
      const endDecimalPart = endDecimalInfo.value;
      const endDecimalDigits = endDecimalInfo.digits;
      
      // 小数部分を即座に設定
      setDisplayDecimalPart(endDecimalPart);
      setShowDecimal(endDecimalDigits > 0);
      setIsAnimatingDecimal(false);
      
      // 現在値を更新（符号は props の isNegative と表示の endValue の両方に整合させる）
      setDisplayIsNegative(isNegative);
      previousValue.current = endValue;
      previousIsNegative.current = isNegative;
      
      // アニメーション完了イベントを発火（必要な場合）
      if (isGroupSubtotal) {
        const event = new CustomEvent('pouches-subtotal-animation-complete', {
          detail: { groupId: groupIdRef.current }
        });
        document.dispatchEvent(event);
      }
      
      previousSuffixRef.current = suffix;
      return;
    }
    
    // 整数部分と小数部分を分離
    const rawStartIntegerPart = Math.floor(Math.abs(startValue));
    const endIntegerPart = Math.floor(Math.abs(endValue));

    const prevSuffix = previousSuffixRef.current;
    const toPercentMode =
      bridgePercentModeTransition &&
      prevSuffix === '' &&
      suffix === '%';
    const fromPercentMode =
      bridgePercentModeTransition &&
      prevSuffix === '%' &&
      suffix === '';

    const usePercentModeBridge =
      toPercentMode &&
      !signChanged &&
      rawStartIntegerPart !== endIntegerPart;

    const startDecimalInfo = getDecimalPart(startValue);
    const endDecimalInfo = getDecimalPart(endValue);
    
    const startDecimalPart = startDecimalInfo.value;
    const endDecimalPart = endDecimalInfo.value;
    const endDecimalDigits = endDecimalInfo.digits;
    
    // 最終値に小数部分があるかどうか
    const hasEndDecimal = endDecimalDigits > 0;

    const integerAnimStart = rawStartIntegerPart;
    
    // 整数部分のアニメーション関数
    const animateInteger = (timestamp: number) => {
      // 新しい値に変更された場合はアニメーションを中止
      if (currentAnimationValue.current !== endValue) {
        return;
      }
      
      // 整数部分のアニメーション時間（全体の2/3）
      const integerDuration = Math.floor(effectiveDuration * 0.67);
      
      if (!startTime.current) {
        startTime.current = timestamp;
      }
      const elapsed = timestamp - startTime.current;
      
      // 符号が変わるかどうかを判定
      const signChanged = (startValue < 0) !== (endValue < 0);
      
      // 整数部分のアニメーション終了
      if (elapsed >= integerDuration || (integerAnimStart === endIntegerPart && !signChanged)) {
        setDisplayIntegerPart(endIntegerPart);
        animationFrameId.current = null;
        
        // 整数部分が同じか、アニメーションが完了したら小数部分のアニメーションを開始
        if (hasEndDecimal) {
          setIsAnimatingDecimal(true);
          setShowDecimal(true);
          decimalStartTime.current = null;
          decimalAnimationFrameId.current = requestAnimationFrame(animateDecimal);
        } else {
          // 小数部分がない場合は表示しない
          setIsAnimatingDecimal(false);
          setShowDecimal(false);
          previousValue.current = endValue;
          previousIsNegative.current = isNegative;
          
          // animateFromZeroがtrueの場合はアニメーション完了イベントを発火
          if (animateFromZero && groupIdRef.current) {
            const event = new CustomEvent('pouches-subtotal-animation-complete', {
              detail: { groupId: groupIdRef.current }
            });
            document.dispatchEvent(event);
          }
        }
        return;
      }
      
      // 整数アニメーション中は小数を非表示
      setShowDecimal(false);
      
      // 整数部分の進捗率を計算（0～1）
      const progress = elapsed / integerDuration;
      
      // 符号が変わる場合の特別処理
      if (signChanged) {
        // 符号が変わる場合は、まず0に向かって減少し、その後符号を変えて増加する
        if (progress < 0.5) {
          // 前半: 現在の値から0に向かって減少
          const firstHalfProgress = progress * 2; // 0～0.5を0～1に変換
          const currentInteger = Math.ceil(rawStartIntegerPart * (1 - firstHalfProgress));
          setDisplayIntegerPart(currentInteger);
          
          // ここで、値が0に近づいたら符号を制御
          if (currentInteger === 0) {
            // 値が0になったら、目標値の符号に合わせる
            setDisplayIsNegative(endValue < 0);
          }
        } else {
          // 後半: 0から目標値に向かって増加
          const secondHalfProgress = (progress - 0.5) * 2; // 0.5～1を0～1に変換
          const currentInteger = Math.floor(endIntegerPart * secondHalfProgress);
          setDisplayIntegerPart(currentInteger);
          
          // 値が0より大きくなったら、目標値の符号に合わせる
          setDisplayIsNegative(endValue < 0);
        }
      } else if (endIntegerPart > integerAnimStart) {
        // 増加する場合は徐々に増加
        const currentInteger = Math.floor(integerAnimStart + (endIntegerPart - integerAnimStart) * progress);
        setDisplayIntegerPart(currentInteger);
      } else {
        // 減少する場合は徐々に減少
        const currentInteger = Math.ceil(integerAnimStart - (integerAnimStart - endIntegerPart) * progress);
        setDisplayIntegerPart(currentInteger);
      }
      
      // 次のフレームを要求
      animationFrameId.current = requestAnimationFrame(animateInteger);
    };
    
    // 小数部分のアニメーション関数
    const animateDecimal = (timestamp: number) => {
      // 新しい値に変更された場合はアニメーションを中止
      if (currentAnimationValue.current !== endValue) {
        return;
      }

      const intForDecimal = decimalCompareIntegerRef.current !== null
        ? decimalCompareIntegerRef.current
        : integerAnimStart;
      
      // 小数部分のアニメーション時間（全体の1/3）
      const decimalDuration = Math.floor(effectiveDuration * 0.33);
      
      if (!decimalStartTime.current) {
        decimalStartTime.current = timestamp;
      }
      const elapsed = timestamp - decimalStartTime.current;
      
      // 小数部分のアニメーション終了
      if (elapsed >= decimalDuration) {
        decimalCompareIntegerRef.current = null;
        setBridgeVisualSuffix(null);
        setDisplayDecimalPart(endDecimalPart);
        setIsAnimatingDecimal(false);
        decimalAnimationFrameId.current = null;
        previousValue.current = endValue;
        previousIsNegative.current = isNegative;
        
        // animateFromZeroがtrueの場合はアニメーション完了イベントを発火
        if (animateFromZero && groupIdRef.current) {
          const event = new CustomEvent('pouches-subtotal-animation-complete', {
            detail: { groupId: groupIdRef.current }
          });
          document.dispatchEvent(event);
        }
        
        return;
      }
      
      // 小数部分の進捗率を計算（0～1）
      const progress = elapsed / decimalDuration;
      
      // 小数点以下の桁数を判断
      const isOneDecimalDigit = endDecimalDigits === 1;
      const isTwoDecimalDigits = endDecimalDigits === 2;
      
      // 整数部分の増減に基づいて小数部分のアニメーション方法を決定
      if (endIntegerPart > intForDecimal) {
        // 整数部分が増加する場合、小数部分は0から目標値まで増加
        const currentDecimal = Math.floor(endDecimalPart * progress);
        setDisplayDecimalPart(currentDecimal);
      } else if (endIntegerPart < intForDecimal) {
        // 整数部分が減少する場合
        // 小数点以下1桁の場合は9から、2桁の場合は99から減少
        const startDecimal = isOneDecimalDigit ? 9 : (isTwoDecimalDigits ? 99 : 0);
        const currentDecimal = Math.floor(startDecimal - (startDecimal - endDecimalPart) * progress);
        setDisplayDecimalPart(currentDecimal);
      } else {
        // 整数部分が変わらない場合、現在の小数部分から目標値まで増減
        const currentDecimal = Math.floor(startDecimalPart + (endDecimalPart - startDecimalPart) * progress);
        setDisplayDecimalPart(currentDecimal);
      }
      
      // 次のフレームを要求
      decimalAnimationFrameId.current = requestAnimationFrame(animateDecimal);
    };

    // %→× の第3段階: 1→目的の整数だけアニメ（100・1 は直前で瞬時表示済み）
    const animateIntegerFromOneToEnd = (timestamp: number) => {
      if (currentAnimationValue.current !== endValue) {
        return;
      }
      const integerDuration = Math.floor(effectiveDuration * 0.67);
      if (!startTime.current) {
        startTime.current = timestamp;
      }
      const elapsed = timestamp - startTime.current;
      const signChangedInner = (startValue < 0) !== (endValue < 0);

      if (elapsed >= integerDuration) {
        setDisplayIntegerPart(endIntegerPart);
        animationFrameId.current = null;
        if (hasEndDecimal) {
          decimalCompareIntegerRef.current = endIntegerPart;
          setIsAnimatingDecimal(true);
          setShowDecimal(true);
          decimalStartTime.current = null;
          decimalAnimationFrameId.current = requestAnimationFrame(animateDecimal);
        } else {
          decimalCompareIntegerRef.current = null;
          setIsAnimatingDecimal(false);
          setShowDecimal(false);
          setBridgeVisualSuffix(null);
          previousValue.current = endValue;
          previousIsNegative.current = isNegative;
          if (animateFromZero && groupIdRef.current) {
            const event = new CustomEvent('pouches-subtotal-animation-complete', {
              detail: { groupId: groupIdRef.current }
            });
            document.dispatchEvent(event);
          }
        }
        return;
      }

      setShowDecimal(false);
      const progress = elapsed / integerDuration;
      if (signChangedInner) {
        setDisplayIntegerPart(endIntegerPart);
      } else if (endIntegerPart > 1) {
        const currentInteger = Math.floor(1 + (endIntegerPart - 1) * progress);
        setDisplayIntegerPart(currentInteger);
      } else if (endIntegerPart < 1) {
        const currentInteger = Math.ceil(1 - (1 - endIntegerPart) * progress);
        setDisplayIntegerPart(currentInteger);
      } else {
        setDisplayIntegerPart(1);
      }
      animationFrameId.current = requestAnimationFrame(animateIntegerFromOneToEnd);
    };

    // 整数 A→B のみアニメ（ブリッジの各段で使用）
    const runIntegerAnim = (from: number, to: number, onComplete: () => void) => {
      if (from === to) {
        onComplete();
        return;
      }
      startTime.current = null;
      const tick = (ts: number) => {
        if (currentAnimationValue.current !== endValue) {
          return;
        }
        const integerDuration = Math.floor(effectiveDuration * 0.67);
        if (!startTime.current) {
          startTime.current = ts;
        }
        const elapsed = ts - startTime.current;
        if (elapsed >= integerDuration) {
          setDisplayIntegerPart(to);
          animationFrameId.current = null;
          startTime.current = null;
          onComplete();
          return;
        }
        const progress = elapsed / integerDuration;
        const cur =
          to > from
            ? Math.floor(from + (to - from) * progress)
            : Math.ceil(from - (from - to) * progress);
        setDisplayIntegerPart(cur);
        setShowDecimal(false);
        animationFrameId.current = requestAnimationFrame(tick);
      };
      animationFrameId.current = requestAnimationFrame(tick);
    };

    // ×→% : ×N→→×1 → >100% → →目的%（→はアニメ、>は即時）
    if (toPercentMode && usePercentModeBridge && !signChanged) {
      previousSuffixRef.current = suffix;
      setBridgeVisualSuffix('');

      const finishToPercent = () => {
        setBridgeVisualSuffix(null);
        previousValue.current = endValue;
        previousIsNegative.current = isNegative;
        if (animateFromZero && groupIdRef.current) {
          const event = new CustomEvent('pouches-subtotal-animation-complete', {
            detail: { groupId: groupIdRef.current }
          });
          document.dispatchEvent(event);
        }
      };

      const phase3Anim100ToEnd = () => {
        setBridgeVisualSuffix('%');
        runIntegerAnim(100, endIntegerPart, () => {
          setDisplayIntegerPart(endIntegerPart);
          animationFrameId.current = null;
          if (hasEndDecimal) {
            decimalCompareIntegerRef.current = endIntegerPart;
            setIsAnimatingDecimal(true);
            setShowDecimal(true);
            decimalStartTime.current = null;
            decimalAnimationFrameId.current = requestAnimationFrame(animateDecimal);
          } else {
            finishToPercent();
          }
        });
      };

      const phase2Instant100Percent = () => {
        setBridgeVisualSuffix('%');
        setDisplayIntegerPart(100);
        setShowDecimal(false);
        setIsAnimatingDecimal(false);
        setDisplayIsNegative(isNegative);
        bridgeSecondLegTimeoutId.current = setTimeout(() => {
          bridgeSecondLegTimeoutId.current = null;
          startTime.current = null;
          phase3Anim100ToEnd();
        }, 0);
      };

      if (rawStartIntegerPart > 1) {
        runIntegerAnim(rawStartIntegerPart, 1, phase2Instant100Percent);
      } else {
        phase2Instant100Percent();
      }

      return () => {
        if (bridgeSecondLegTimeoutId.current) {
          clearTimeout(bridgeSecondLegTimeoutId.current);
          bridgeSecondLegTimeoutId.current = null;
        }
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }
        if (decimalAnimationFrameId.current) {
          cancelAnimationFrame(decimalAnimationFrameId.current);
        }
      };
    }

    // %→× : 12%→→100% → >×1 → →目的（→はアニメ、>は即時）
    if (
      fromPercentMode &&
      !signChanged &&
      rawStartIntegerPart !== endIntegerPart
    ) {
      previousSuffixRef.current = suffix;
      setBridgeVisualSuffix('%');

      const finishSubtotalIfNeeded = () => {
        if (isGroupSubtotal) {
          const event = new CustomEvent('pouches-subtotal-animation-complete', {
            detail: { groupId: groupIdRef.current }
          });
          document.dispatchEvent(event);
        }
      };

      const afterOneInstant = () => {
        setBridgeVisualSuffix(null);
        setDisplayIntegerPart(1);
        setShowDecimal(false);
        setIsAnimatingDecimal(false);
        setDisplayIsNegative(isNegative);

        if (endIntegerPart === 1) {
          const endDecimalInfoJump = getDecimalPart(endValue);
          setDisplayDecimalPart(endDecimalInfoJump.value);
          setShowDecimal(endDecimalInfoJump.digits > 0);
          previousValue.current = endValue;
          previousIsNegative.current = isNegative;
          finishSubtotalIfNeeded();
          return;
        }

        bridgeSecondLegTimeoutId.current = setTimeout(() => {
          bridgeSecondLegTimeoutId.current = null;
          startTime.current = null;
          animationFrameId.current = requestAnimationFrame(animateIntegerFromOneToEnd);
        }, 0);
      };

      const phase1Done = () => {
        bridgeSecondLegTimeoutId.current = setTimeout(() => {
          bridgeSecondLegTimeoutId.current = null;
          afterOneInstant();
        }, 0);
      };

      if (rawStartIntegerPart < 100) {
        runIntegerAnim(rawStartIntegerPart, 100, phase1Done);
      } else {
        phase1Done();
      }

      return () => {
        if (bridgeSecondLegTimeoutId.current) {
          clearTimeout(bridgeSecondLegTimeoutId.current);
          bridgeSecondLegTimeoutId.current = null;
        }
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }
        if (decimalAnimationFrameId.current) {
          cancelAnimationFrame(decimalAnimationFrameId.current);
        }
      };
    }
    
    // 初期状態の設定
    const kickOffIntegerOrDecimal = () => {
      const finalValueHasDecimal = endDecimalDigits > 0;
      setShowDecimal(integerAnimStart === endIntegerPart && finalValueHasDecimal);

      if (integerAnimStart === endIntegerPart && startDecimalPart !== endDecimalPart && !signChanged) {
        setDisplayIntegerPart(endIntegerPart);
        setIsAnimatingDecimal(true);
        setShowDecimal(finalValueHasDecimal);
        decimalStartTime.current = null;
        decimalAnimationFrameId.current = requestAnimationFrame(animateDecimal);
      } else if (integerAnimStart !== endIntegerPart || signChanged) {
        startTime.current = null;
        animationFrameId.current = requestAnimationFrame(animateInteger);
      }
    };

    kickOffIntegerOrDecimal();

    previousSuffixRef.current = suffix;

    // クリーンアップ
    return () => {
      if (bridgeSecondLegTimeoutId.current) {
        clearTimeout(bridgeSecondLegTimeoutId.current);
        bridgeSecondLegTimeoutId.current = null;
      }
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      if (decimalAnimationFrameId.current) {
        cancelAnimationFrame(decimalAnimationFrameId.current);
      }
    };
  }, [actualValue, isNegative, _animateSignChange, suffix, bridgePercentModeTransition]);
  
  // コンポーネントのアンマウント時にアニメーションをクリーンアップ
  useEffect(() => {
    return () => {
      if (bridgeSecondLegTimeoutId.current) {
        clearTimeout(bridgeSecondLegTimeoutId.current);
        bridgeSecondLegTimeoutId.current = null;
      }
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      if (decimalAnimationFrameId.current) {
        cancelAnimationFrame(decimalAnimationFrameId.current);
      }
    };
  }, []);
  
  
  // 小数部分のフォーマット（桁数に応じて処理）
  let formattedDecimal = '';
  if (initialDecimalInfo.digits === 1) {
    // 1桁の場合（1-9）
    formattedDecimal = displayDecimalPart.toString();
  } else if (initialDecimalInfo.digits === 2) {
    // 2桁の場合（0-99）- ゼロ埋めして2桁表示
    formattedDecimal = displayDecimalPart.toString().padStart(2, '0');
  }
  
  // 四捨五入が必要か確認（小数第3位以上の数字があるか）
  const absValue = Math.abs(actualValue);
  const hasDecimal = absValue !== Math.floor(absValue);
  const needsRounding = hasDecimal && 
                       (absValue.toFixed(6) !== absValue.toFixed(2) + '0000');

  // 符号表示のためのマーカー関数（JSXのレンダリング部分で使用）
  const shouldShowNegativeSign = () => {
    // アニメーションで符号が変わる場合、displayIntegerPartが0でなければ符号を表示
    return displayIsNegative && displayIntegerPart !== 0;
  };

  // クラス名を設定
  const finalClassName = `number-counter ${className} ${shouldShowNegativeSign() ? 'negative' : ''} ${needsRounding ? 'rounded' : ''}`.trim();
  
  // ツールチップ用のtitle属性を生成
  const getTooltipTitle = () => {
    if (tooltipContent) {
      return tooltipContent;
    }
    
    if (needsRounding) {
      // まず整数かどうかをチェック
      if (Math.abs(actualValue - Math.round(actualValue)) < 1e-10) {
        // 整数の場合（浮動小数点の誤差を考慮）
        const integerValue = Math.round(actualValue);
        return `${integerValue < 0 ? '-' : ''}${Math.abs(integerValue).toLocaleString()}`;
      }
      
      // 通常の処理（8桁で丸めて9桁目以降をチェック）
      const roundedValue8 = Math.round(actualValue * 100000000) / 100000000;
      const roundedValue9 = Math.round(actualValue * 1000000000) / 1000000000;
      const hasMoreDigits = Math.abs(roundedValue9 - roundedValue8) > 0;
      
      // デバッグ用ログ
      
      // 整数部分と小数部分を分離
      const integerPart = Math.floor(Math.abs(roundedValue8));
      const decimalPart = Math.abs(roundedValue8) - integerPart;
      
      // 整数部分に3桁カンマ区切りを適用
      const formattedInteger = integerPart.toLocaleString();
      
      // 小数部分がある場合は結合
      let result = '';
      if (decimalPart > 0) {
        // 小数部分を8桁まで表示（末尾の0は除去）
        const decimalString = decimalPart.toFixed(8).replace(/0+$/, '');
        result = `${roundedValue8 < 0 ? '-' : ''}${formattedInteger}.${decimalString.substring(2)}`;
      } else {
        result = `${roundedValue8 < 0 ? '-' : ''}${formattedInteger}`;
      }
      
      // 9桁目以降がある場合は"..."を追加
      if (hasMoreDigits) {
        result += '...';
      }
      

      
      // 時刻変換が有効な場合は追加
      if (showTimeConversion) {
        // 実際の値（actualValue）を使用して時刻変換
        const totalMinutes = Math.round(Math.abs(actualValue));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const timeConversion = ` (${hours}:${minutes.toString().padStart(2, '0')})`;
        result += timeConversion;
      }
      
      return result;
    }
    
    // 時刻変換のみの場合（丸め処理が不要だが時刻変換は必要）
    if (showTimeConversion) {
      // 実際の値（actualValue）を使用して時刻変換
      const totalMinutes = Math.round(Math.abs(actualValue));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const timeConversion = `(${hours}:${minutes.toString().padStart(2, '0')})`;
      
      const formattedValue = Math.abs(actualValue).toLocaleString();
      return `${actualValue < 0 ? '-' : ''}${formattedValue} ${timeConversion}`;
    }
    
    return '';
  };
  
  return (
    <span 
      className={finalClassName}
      {...(suppressTooltip ? {} : tooltipHtml ? {'data-tooltip-id': 'tt', 'data-tooltip-html': tooltipHtml} : getTooltipTitle() ? {'data-tooltip-id': 'tt', 'data-tooltip-html': `<b>${getTooltipTitle()}</b>`} : {})} {...(tooltipPlace ? {'data-tooltip-place': tooltipPlace} : {})}
    >
      {shouldShowNegativeSign() ? '-' : ''}
      {Math.abs(displayIntegerPart).toLocaleString()}
      {showDecimal && formattedDecimal !== '' && (
        <>
          <small>.{formattedDecimal}</small>
        </>
      )}
      {bridgeVisualSuffix !== null ? bridgeVisualSuffix : suffix}
    </span>
  );
}; 

export default NumberCounter; 